"use client";

import { useEffect, useId, useState } from "react";

/**
 * Falten-Zeile (PM Paket 3b, B4, Design-Guide `falten-zeile.md`): EINE Zeilenform für nachgeordnete Bereiche des Aufgaben-Details
 * (Rückverweise, Themenpool, ISMS-Maßnahme, Hornaut). Kopf mit Plus/Minus, Titel und optionalem Zähler; der Inhalt steht erst nach dem
 * Aufklappen im Bild und wird erst dann gerendert (`kinder` als Funktion oder Knoten; Daten mit Server-Bezug lädt der Inhalt selbst).
 * Zustand liegt in React (wie AW-020), das Markup folgt `component/accordion/accordion-always-open`; mehrere Zeilen können offen sein.
 *
 * KI-Zeile (`ki`, R-D r22): die ganze Zeile trägt `ai-gated` und `data-ki="gated"`, der Titel das Symbol „Sparkles“ (KI-Kennzeichnung,
 * R-F). Bei ausgeschalteter KI (`kiAktiv=false`, serverseitig aus Rolle und Richtlinie) steht anstelle des Inhalts der Satz `kiAusHinweis`
 * (Pflicht-Fallback, kein stilles Verschwinden). Auslöser im Inhalt tragen `ai-gated ai-mark` (`Knopf ki`).
 * Kopf wie die Vorschau b04: KI-Zeile mit Symbol und Text „KI“, rechtsbündiger `hinweis`. Abweichung AW-040 (Plus/Minus statt Pfeil,
 * Titelgröße 16 px/600 statt kleiner). Abweichung AW-024: Zusammenbau aus dem Accordion (Komponente) mit Zähler und KI-Marker; der Katalog hat keine KI-Zeile.
 *
 * Aufgaben-Detail A4′ (Betreiber 14.10.2026, Musterkatalog M-021 B4-D/B5), additiv:
 *  - `variante="bezug"`: Zeile der Karte „Bezüge“ (Symbol im Rahmen, Titel, rechts Zahl oder Kurztext, Pfeil nach rechts, der beim Öffnen
 *    nach unten dreht). Alle Zeilen der Karte haben diese eine Form, auch die KI-Zeile (Symbol in den KI-Tokens `ki-soft/ki-line/ki-text`,
 *    Kopf trägt `ai-mark`).
 *  - `offen` + `beimUmschalten`: gesteuerter Betrieb (Öffnen von außen: „Verknüpfen …“ am Brett, Teilaufgaben-Falte ab `lg` offen).
 *  - `kopfZusatz`: Element hinter dem Titel (Ring und „1/4 erledigt“ der Teilaufgaben-Falte); `hinweisTon="gefahr"`: der rechtsbündige
 *    Hinweis in Rot („1 überfällig“), mit Punkt-Marker, nie Farbe allein.
 */
const SPARKLES = (
  <svg className="shrink-0 size-4 text-ki" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
    <path d="M4 17v2" />
    <path d="M5 18H3" />
  </svg>
);

const KOPF =
  "py-3 inline-flex items-center gap-x-3 w-full font-semibold text-start text-foreground hover:text-muted-foreground-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-lg disabled:opacity-50 disabled:pointer-events-none";
const KOPF_BEZUG =
  "py-2.5 px-4 flex items-center gap-x-3 w-full text-start text-foreground hover:bg-muted-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-focus";
const KOPF_BEZUG_KI =
  "py-2.5 px-4 flex items-center gap-x-3 w-full text-start text-foreground hover:bg-muted-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-focus ai-mark";
const SYMBOL_KASTEN = "flex shrink-0 justify-center items-center size-7 rounded-md border border-layer-line bg-layer text-muted-foreground-1";
const SYMBOL_KASTEN_KI = "flex shrink-0 justify-center items-center size-7 rounded-md border border-ki-line bg-ki-soft text-ki-text";

export function FaltenZeile({
  titel,
  kurzhinweis,
  zaehler,
  hinweis,
  hinweisTon,
  kopfZusatz,
  variante = "standard",
  symbol,
  ki = false,
  kiAktiv = true,
  kiAusHinweis,
  offenStart = false,
  offen: offenVorgabe,
  beimUmschalten,
  beimOeffnen,
  children,
}: {
  titel: string;
  /** Optional, nur Standardform: `title` am (gekürzten) Titel, damit bei schmaler Spalte der volle Text lesbar bleibt. Standard: keiner. */
  kurzhinweis?: string;
  /** Zahl hinter dem Titel (Treffer); `undefined` = keine Angabe. */
  zaehler?: number;
  /** Kurzer Hinweis rechtsbündig im Kopf („Vorschlag, du entscheidest“, bei `bezug` die Zahl oder der Stand); reiner Text, kein Zustand. */
  hinweis?: string;
  /** Nur `gefahr`: der Hinweis steht in Rot mit Punkt-Marker („1 überfällig“). */
  hinweisTon?: "gefahr";
  /** Element hinter dem Titel (z. B. Ring und Zählertext der Teilaufgaben). */
  kopfZusatz?: React.ReactNode;
  /** `bezug`: Zeile der Karte „Bezüge“ (M-021 B5). */
  variante?: "standard" | "bezug";
  /** Symbol im Rahmen links (nur `bezug`, Lucide, `aria-hidden`); bei `ki` ersetzt das Sparkles-Symbol der KI-Kennzeichnung. */
  symbol?: React.ReactNode;
  /** Zeile mit KI-Inhalt (R-D). */
  ki?: boolean;
  /** Nur bei `ki`: KI für diese Person verfügbar (Rolle und Richtlinie). */
  kiAktiv?: boolean;
  /** Nur bei `ki`: Satz bei ausgeschalteter KI. */
  kiAusHinweis?: string;
  offenStart?: boolean;
  /** Gesteuert: der Aufrufer hält den Zustand (dann gilt `beimUmschalten`). */
  offen?: boolean;
  beimUmschalten?: (offen: boolean) => void;
  /** Wird beim ersten Aufklappen gerufen (z. B. Daten laden). */
  beimOeffnen?: () => void;
  children: React.ReactNode;
}) {
  const [offenIntern, setOffen] = useState(offenStart);
  const [geoeffnetEinmal, setGeoeffnetEinmal] = useState(offenStart);
  const offen = offenVorgabe ?? offenIntern;
  const id = useId();
  const umschalten = () => {
    const neu = !offen;
    setOffen(neu);
    beimUmschalten?.(neu);
    if (neu && !geoeffnetEinmal) {
      setGeoeffnetEinmal(true);
      beimOeffnen?.();
    }
  };
  // Von außen geöffnet (gesteuert): das erste Öffnen löst `beimOeffnen` trotzdem einmal aus (Daten laden).
  useEffect(() => {
    if (offenVorgabe && !geoeffnetEinmal) {
      setGeoeffnetEinmal(true);
      beimOeffnen?.();
    }
  }, [offenVorgabe, geoeffnetEinmal, beimOeffnen]);
  const bezug = variante === "bezug";
  const huelle = ki ? "ai-gated border-t border-line-2 first:border-t-0" : "border-t border-line-2 first:border-t-0";
  const kiDaten = ki ? { "data-ki": "gated", "data-ki-aktiv": kiAktiv ? "an" : "aus" } : {};
  const inhalt = (
    <div id={`${id}-inhalt`} role="region" aria-labelledby={`${id}-kopf`} hidden={!offen} className={bezug ? "w-full px-4 pb-3" : "w-full pb-3"}>
      {offen &&
        (ki && !kiAktiv ? (
          <p className="text-sm text-muted-foreground-1" role="status">
            {kiAusHinweis}
          </p>
        ) : (
          children
        ))}
    </div>
  );
  const hinweisKnoten =
    hinweis &&
    (hinweisTon === "gefahr" ? (
      <span className="ms-auto shrink-0 inline-flex items-center gap-x-1 whitespace-nowrap text-xs font-medium text-destructive">
        <span className="size-2 inline-block rounded-full bg-current" aria-hidden="true" />
        {hinweis}
      </span>
    ) : (
      <span className={bezug ? "ms-auto shrink-0 whitespace-nowrap text-xs font-normal text-muted-foreground-1" : "ms-auto min-w-0 truncate text-xs font-normal text-muted-foreground-1"}>{hinweis}</span>
    ));

  if (bezug) {
    return (
      <>
        {/* Preline: component/accordion/accordion-always-open (Zeilenform der Karte „Bezüge“, M-021 B5) */}
        <div className={huelle} {...kiDaten}>
          <h3>
            <button type="button" className={ki ? KOPF_BEZUG_KI : KOPF_BEZUG} aria-expanded={offen} aria-controls={`${id}-inhalt`} id={`${id}-kopf`} onClick={umschalten}>
              <span className={ki ? SYMBOL_KASTEN_KI : SYMBOL_KASTEN} aria-hidden="true">
                {symbol ?? (ki ? SPARKLES : null)}
              </span>
              <span className="grow min-w-0 truncate text-sm font-medium">
                {titel}
                {ki && <span className="ms-2 text-xs font-medium text-ki-text">KI</span>}
              </span>
              {zaehler !== undefined && hinweis === undefined && <span className="shrink-0 text-xs font-normal text-muted-foreground-1">{zaehler}</span>}
              {hinweisKnoten}
              <svg className={offen ? "shrink-0 size-4 text-muted-foreground-1 rotate-90" : "shrink-0 size-4 text-muted-foreground-1"} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </h3>
          {inhalt}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Preline: component/accordion/accordion-always-open */}
      <div className={huelle} {...kiDaten}>
        <h3>
          <button type="button" className={KOPF} aria-expanded={offen} aria-controls={`${id}-inhalt`} id={`${id}-kopf`} onClick={umschalten}>
            {offen ? (
              <svg className="block size-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
              </svg>
            ) : (
              <svg className="block size-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
            )}
            {ki && SPARKLES}
            <span className="min-w-0 truncate" title={kurzhinweis}>{titel}</span>
            {ki && <span className="shrink-0 text-xs font-medium text-muted-foreground-1">KI</span>}
            {zaehler !== undefined && <span className="text-xs font-normal text-muted-foreground-1">{zaehler}</span>}
            {kopfZusatz}
            {hinweisKnoten}
          </button>
        </h3>
        {inhalt}
      </div>
    </>
  );
}
